import { useSendEmailCode } from "@kupikupi/api";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import "../styles/e-auth.css";

// Step-progress indicator
function AuthSteps({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div className="e-auth-steps">
        <span
          className={`e-auth-steps__s${current > 1 ? " is-done" : current === 1 ? " is-current" : ""}`}
        />
        <span
          className={`e-auth-steps__s${current > 2 ? " is-done" : current === 2 ? " is-current" : ""}`}
        />
        <span className={`e-auth-steps__s${current === 3 ? " is-current" : ""}`} />
      </div>
    </div>
  );
}

// Mask email for display: xiao***wang@gmail.com
function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local.slice(0, 2)}***${local.slice(-2)}@${domain}`;
}

// 6-digit OTP input grid
function OtpInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  const handleInput = (i: number, raw: string) => {
    const char = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = char;
    onChange(next.join(""));
    if (char && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!digits[i] && i > 0) {
        const next = [...digits];
        next[i - 1] = "";
        onChange(next.join(""));
        refs.current[i - 1]?.focus();
      } else if (digits[i]) {
        const next = [...digits];
        next[i] = "";
        onChange(next.join(""));
      }
    }
  };

  return (
    <div
      className="e-otp"
      style={{ gridTemplateColumns: "repeat(6,1fr)", maxWidth: "none" }}
    >
      {digits.map((d, i) => (
        <input
          // biome-ignore lint/suspicious/noArrayIndexKey: stable positional OTP grid
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          placeholder=""
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          style={{ caretColor: "transparent" }}
        />
      ))}
    </div>
  );
}

// ─── Step 1: Email entry ───────────────────────────────────────────────────
function Step1({
  email,
  setEmail,
  onNext,
  sendCode,
}: {
  email: string;
  setEmail: (v: string) => void;
  onNext: () => void;
  sendCode: ReturnType<typeof useSendEmailCode>;
}) {
  const { t } = useTranslation("auth");
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!email.trim() || !sendCode.canSend) return;
    setError(null);
    try {
      // type: 1 = forgot-password email captcha (type: 0 = registration)
      await sendCode.mutateAsync({ email: email.trim(), type: 1 });
      onNext();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("register.send_code_failed"));
    }
  };

  return (
    <>
      <div
        className="tg-body"
        style={{ flex: 1, padding: "8px 20px 24px", gap: 0 }}
      >
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 6px",
          }}
        >
          {t("forgot.title")}
        </h1>
        <p
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            margin: "0 0 14px",
          }}
        >
          <strong style={{ color: "var(--color-primary-fill)" }}>1</strong>{" "}
          / 3 · {t("forgot.step1_title")}
        </p>

        <AuthSteps current={1} />

        <div
          style={{
            fontFamily: "monospace",
            fontSize: 10,
            color: "var(--color-text-muted)",
            marginBottom: 18,
          }}
        >
          <strong style={{ color: "var(--color-text)" }}>
            {t("forgot.step1_title")}
          </strong>
          {" · "}
          {t("forgot.step2_title")}
          {" · "}
          {t("forgot.step3_title")}
        </div>

        <label
          className="e-field__l"
          style={{ fontSize: 11, display: "block", marginBottom: 6 }}
          htmlFor="forgot-email"
        >
          {t("forgot.email")} <span style={{ color: "var(--color-price)" }}>*</span>
        </label>
        <input
          id="forgot-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("forgot.email_placeholder")}
          style={{
            width: "100%",
            border: "1.5px solid var(--color-border)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            fontFamily: "inherit",
            background: "#fff",
            boxSizing: "border-box",
            outline: "none",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "var(--color-primary-fill)";
            e.currentTarget.style.boxShadow = "0 0 0 3px var(--color-focus-ring)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border)";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
        {error && (
          <p
            style={{
              fontSize: 11,
              color: "var(--color-danger)",
              margin: "6px 0 0",
            }}
          >
            {error}
          </p>
        )}
      </div>

      <TgMain
        disabled={!email.trim() || !sendCode.canSend}
        onClick={handleSend}
      >
        {sendCode.isPending
          ? t("register.send_code_sending")
          : t("forgot.send_code")}
      </TgMain>
    </>
  );
}

// ─── Step 2: OTP verification ──────────────────────────────────────────────
function Step2({
  email,
  otp,
  setOtp,
  onNext,
  onBack,
  sendCode,
}: {
  email: string;
  otp: string;
  setOtp: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  sendCode: ReturnType<typeof useSendEmailCode>;
}) {
  const { t } = useTranslation("auth");

  const handleResend = async () => {
    if (!sendCode.canSend) return;
    try {
      await sendCode.mutateAsync({ email, type: 1 });
    } catch {
      // silently ignore — backend sends new code anyway
    }
  };

  const canProceed = otp.length === 6;

  return (
    <>
      <div
        className="tg-body"
        style={{ flex: 1, padding: "8px 20px 24px", gap: 0 }}
      >
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 6px",
          }}
        >
          {t("forgot.title")}
        </h1>
        <p
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            margin: "0 0 14px",
          }}
        >
          <strong style={{ color: "var(--color-primary-fill)" }}>2</strong>
          {" / 3 · "}
          {t("forgot.step2_title")}
        </p>

        <AuthSteps current={2} />

        <div
          style={{
            fontFamily: "monospace",
            fontSize: 10,
            color: "var(--color-text-muted)",
            marginBottom: 18,
          }}
        >
          {t("forgot.step1_title")}
          {" · "}
          <strong style={{ color: "var(--color-text)" }}>
            {t("forgot.step2_title")}
          </strong>
          {" · "}
          {t("forgot.step3_title")}
        </div>

        {/* Email preview card */}
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "10px 12px",
            background: "var(--color-surface)",
            borderRadius: 10,
            border: "1px solid var(--color-border)",
            marginBottom: 18,
            alignItems: "center",
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "var(--color-primary-soft)",
              color: "var(--color-primary-fill)",
              display: "grid",
              placeItems: "center",
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            📧
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {maskEmail(email)}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--color-text-muted)",
                marginTop: 1,
              }}
            >
              {sendCode.cooldown > 0
                ? t("register.send_code_cooldown", { sec: sendCode.cooldown })
                : t("forgot.code_valid_hint")}
            </div>
          </div>
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 10,
              color: "var(--color-primary-fill)",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {t("forgot.sent_label")}
          </span>
        </div>

        {/* OTP input */}
        <label
          className="e-field__l"
          style={{ fontSize: 11, display: "block", marginBottom: 8 }}
        >
          {t("forgot.code")}{" "}
          <span style={{ color: "var(--color-price)" }}>*</span>
        </label>
        <OtpInput value={otp} onChange={setOtp} />

        {/* Resend + back */}
        <div className="e-otp-resend">
          <span style={{ fontSize: 10 }}>
            {sendCode.cooldown > 0 ? (
              <span>
                {/* not-received hint when cooling down */}
                <a href="#" className="is-disabled" onClick={(e) => e.preventDefault()}>
                  {t("register.send_code_cooldown", { sec: sendCode.cooldown })}
                </a>
              </span>
            ) : (
              <span>
                {/* biome-ignore lint/a11y/useButtonType: link-like trigger */}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={sendCode.isPending}
                  style={{
                    background: "none",
                    border: 0,
                    padding: 0,
                    color: "var(--color-primary-fill)",
                    fontWeight: 700,
                    fontSize: 10,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {sendCode.isPending
                    ? t("register.send_code_sending")
                    : t("forgot.resend")}
                </button>
              </span>
            )}
          </span>
          {/* biome-ignore lint/a11y/useButtonType: link-like back trigger */}
          <button
            type="button"
            onClick={onBack}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              color: "var(--color-text-muted)",
              fontSize: 10,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            ← {t("forgot.step1_title")}
          </button>
        </div>
      </div>

      <TgMain disabled={!canProceed} onClick={onNext}>
        {t("forgot.step2_title")} →
      </TgMain>
    </>
  );
}

// ─── Step 3: New password ──────────────────────────────────────────────────
function Step3({
  onSubmit,
}: {
  onSubmit: (newPassword: string) => void;
}) {
  const { t } = useTranslation("auth");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) {
      setError(t("forgot.passwords_mismatch"));
      return;
    }
    if (newPassword.length < 8) {
      setError(t("register.password_too_short"));
      return;
    }
    setError(null);
    onSubmit(newPassword);
  };

  const inputStyle = {
    width: "100%",
    border: "1.5px solid var(--color-border)",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    fontFamily: "inherit",
    background: "#fff",
    boxSizing: "border-box" as const,
    outline: "none",
  };

  return (
    <>
      <div
        className="tg-body"
        style={{ flex: 1, padding: "8px 20px 24px", gap: 0 }}
      >
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 6px",
          }}
        >
          {t("forgot.title")}
        </h1>
        <p
          style={{
            fontSize: 11,
            color: "var(--color-text-muted)",
            margin: "0 0 14px",
          }}
        >
          <strong style={{ color: "var(--color-primary-fill)" }}>3</strong>
          {" / 3 · "}
          {t("forgot.step3_title")}
        </p>

        <AuthSteps current={3} />

        <div
          style={{
            fontFamily: "monospace",
            fontSize: 10,
            color: "var(--color-text-muted)",
            marginBottom: 18,
          }}
        >
          {t("forgot.step1_title")}
          {" · "}
          {t("forgot.step2_title")}
          {" · "}
          <strong style={{ color: "var(--color-text)" }}>
            {t("forgot.step3_title")}
          </strong>
        </div>

        <label
          htmlFor="forgot-new-pw"
          className="e-field__l"
          style={{ fontSize: 11, display: "block", marginBottom: 6 }}
        >
          {t("forgot.new_password")}{" "}
          <span style={{ color: "var(--color-price)" }}>*</span>
        </label>
        <input
          id="forgot-new-pw"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          style={{ ...inputStyle, marginBottom: 14 }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "var(--color-primary-fill)";
            e.currentTarget.style.boxShadow = "0 0 0 3px var(--color-focus-ring)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border)";
            e.currentTarget.style.boxShadow = "none";
          }}
        />

        <label
          htmlFor="forgot-confirm-pw"
          className="e-field__l"
          style={{ fontSize: 11, display: "block", marginBottom: 6 }}
        >
          {t("forgot.confirm_password")}{" "}
          <span style={{ color: "var(--color-price)" }}>*</span>
        </label>
        <input
          id="forgot-confirm-pw"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "var(--color-primary-fill)";
            e.currentTarget.style.boxShadow = "0 0 0 3px var(--color-focus-ring)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border)";
            e.currentTarget.style.boxShadow = "none";
          }}
        />

        {error && (
          <p
            style={{
              fontSize: 11,
              color: "var(--color-danger)",
              margin: "8px 0 0",
            }}
          >
            {error}
          </p>
        )}

        {/* NOTE: POST /sys/user/resetPassword endpoint does not exist in
            packages/api. useUpdatePassword requires an auth token and is for
            logged-in password change only. Step 3 submit is disabled until
            backend exposes the reset endpoint. */}
        <div
          style={{
            marginTop: 16,
            padding: "10px 12px",
            background: "var(--color-surface)",
            borderRadius: 10,
            border: "1px solid var(--color-border)",
            fontSize: 11,
            color: "var(--color-text-muted)",
            lineHeight: 1.5,
          }}
        >
          ℹ️ {t("forgot.reset_unavailable")}
        </div>
      </div>

      {/* Submit is disabled — reset endpoint not in API */}
      <TgMain
        disabled
        onClick={handleSubmit}
      >
        {t("forgot.submit")}
      </TgMain>
    </>
  );
}

// ─── Page root ─────────────────────────────────────────────────────────────
export function ForgotPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const sendCode = useSendEmailCode();

  const onStep1Done = () => setStep(2);
  const onStep2Done = () => setStep(3);
  const onBackToStep1 = () => setStep(1);

  const onStep3Submit = (_newPassword: string) => {
    // reset endpoint not available — button is disabled anyway
    navigate({ to: "/login" as never });
  };

  return (
    // Gradient background per design: linear-gradient(180deg,#E0F7F1,#B8E8DD)
    // We set the background on the outer wrapper since TgPage doesn't accept style.
    <div style={{ background: "linear-gradient(180deg,#E0F7F1,#B8E8DD)", minHeight: "100vh" }}>
      <TgPage tabbar={false}>
        <TgHeader title={t("forgot.title")} back borderless />

        {step === 1 && (
          <Step1
            email={email}
            setEmail={setEmail}
            onNext={onStep1Done}
            sendCode={sendCode}
          />
        )}
        {step === 2 && (
          <Step2
            email={email}
            otp={otp}
            setOtp={setOtp}
            onNext={onStep2Done}
            onBack={onBackToStep1}
            sendCode={sendCode}
          />
        )}
        {step === 3 && <Step3 onSubmit={onStep3Submit} />}
      </TgPage>
    </div>
  );
}
